import React, { useMemo } from "react";
import { useLocale, WithLocale } from "@insightfulscience/i18";
import { createAndRaise } from "@insightfulscience/shared-utils/fn/create-and-raise";
import pipe from "@insightfulscience/shared-utils/fn/pipe";
import Hero from "@insightfulscience/atomic-react/Hero";
import HeroTitle from "@insightfulscience/atomic-react/Hero/Title";
import HeroDescription from "@insightfulscience/atomic-react/Hero/Description";
import PageNavbarSearch from "@insightfulscience/atomic-react/PageNavbarSearch";
import PageNavbarSearchMain from "@insightfulscience/atomic-react/PageNavbarSearch/Main";
import PageNavbarSearchSidebar from "@insightfulscience/atomic-react/PageNavbarSearch/Sidebar";
import {
	PlasmidElementSectionBreadcrumbs,
	PlasmidElementSetTitleLink,
	PlasmidElementSetDescription,
} from "@insightfulscience/atomic-react/PlasmidElement";
import Breadcrumbs from "@insightfulscience/atomic-react/Breadcrumbs";
import BreadcrumbItem, {
	BreadcrumbItemLink,
} from "@insightfulscience/atomic-react/Breadcrumbs/Item";
import LandingSimpleSection from "@insightfulscience/atomic-react/LandingElement/SimpleSection";
import { Col, Row } from "@insightfulscience/atomic-react/Grid";
import Paragraph from "@insightfulscience/atomic-react/Paragraph";
import Link from "@insightfulscience/atomic-react/Link";
import UList from "@insightfulscience/atomic-react/UList";
import Video from "@insightfulscience/atomic-react/Video";
import { usePageData } from "@insightfulscience/smart-react/hocs/DiPageHoC";
import NextLink from "@insightfulscience/smart-react/NextLink";
import PageMeta from "@insightfulscience/smart-react/PageMeta";
import WithConditions from "@insightfulscience/smart-react/hocs/WithConditions";
import NextPageWithServices from "@insightfulscience/smart-react/hocs/NextPageWithServices";
import ApplicationError from "@insightfulscience/smart-react/ApplicationError";
import {
	ALIAS_BASIC_CLONING_VECTORS,
	ALIAS_FLUORESCENT_PROTEIN_GENES_AND_PLASMIDS,
	ALIAS_PBR322,
	ALIAS_PEGFP_N1,
	ALIAS_PET_28A_PLUS,
	ALIAS_PET_AND_DUET_VECTORS_NOVAGEN,
	ALIAS_PUC19,
	ROUTE_CONTACT_US,
	ROUTE_DOMAIN_PROD,
	ROUTE_HOME,
	ROUTE_PLASMIDS,
} from "@src/Plasmids/routes";
import { PageNavbarSearchLink } from "@src/Plasmids/ui/PageNavbarSearch";
import SectionItems from "@src/Plasmids/ui/SectionItems";
import BlockAutocomplete from "@src/Plasmids/ui/BlockAutocomplete";
import { routePlasmid } from "@src/Plasmids/utils/plasmid/helpers";
import sgApiConnection from "@src/Plasmids/services/sgApiConnection";
import sgApiClient from "@src/Plasmids/services/sgApiClient";
import resolvers from "@src/Plasmids/resolvers/sets-resolvers";
import { serviceCore } from "@src/Plasmids/services";
import SiteLayout from "@src/SiteLayout";
import SectionTryForFree from "@src/SectionTryForFree";
import { withErrorHandling } from "@src/AppError";

import resources from "../../locales/plasmids-home-page";

const PlasmidsHomePage = () => {
	const [t] = useLocale("plasmids-home");
	const { sets } = usePageData();
	const url = useMemo(() => `https://${ROUTE_DOMAIN_PROD}${ROUTE_PLASMIDS}`, []);
	return (
		<SiteLayout hero isBorderBottomNone>
			<PageMeta title={t("META_TITLE")} description={t("META_DESCRIPTION")}>
				<PageMeta.OGP
					title={t("META_TITLE")}
					description={t("META_DESCRIPTION")}
					siteName={t("META_SITE_NAME")}
					articlePublisher={t("META_ARTICLE_PUBLISHER")}
					locale={t("META_LOCALE")}
					type={t("META_TYPE")}
					url={url}
					image={t("META_IMAGE")}
					imageSecureUrl={t("META_IMAGE_SECURE")}
					isImageBaseUrl
				/>
				<PageMeta.Canonical href={url} />
				<PageMeta.Author content={t("META_AUTHOR")} />
				<PageMeta.Twitter
					title={t("META_TITLE")}
					description={t("META_DESCRIPTION")}
					card={t("META_TWITTER_CARD")}
					creator={t("META_TWITTER_CREATOR")}
					site={t("META_TWITTER_SITE")}
					image={t("META_TWITTER_IMAGE")}
					isImageBaseUrl
				/>
				<PageMeta.Robots content={t("META_ROBOTS")} />
			</PageMeta>
			<Hero>
				<HeroTitle>{t("HERO_TITLE")}</HeroTitle>
				<HeroDescription>{t("HERO_DESCRIPTION")}</HeroDescription>
			</Hero>
			<PageNavbarSearch>
				<PageNavbarSearchMain>
					{t("EXPLORE_OVER_PLASMIDS_INCLUDING")} {/* */}
					<NextLink
						Component={PageNavbarSearchLink}
						href={routePlasmid(ALIAS_PET_AND_DUET_VECTORS_NOVAGEN, ALIAS_PET_28A_PLUS)}
					>
						pET-28a(+)
					</NextLink>
					,{" "}
					<NextLink
						Component={PageNavbarSearchLink}
						href={routePlasmid(ALIAS_BASIC_CLONING_VECTORS, ALIAS_PUC19)}
					>
						pUC19
					</NextLink>
					,{" "}
					<NextLink
						Component={PageNavbarSearchLink}
						href={routePlasmid(ALIAS_BASIC_CLONING_VECTORS, ALIAS_PBR322)}
					>
						pBR322
					</NextLink>
					,{" "}
					<NextLink
						Component={PageNavbarSearchLink}
						href={routePlasmid(ALIAS_FLUORESCENT_PROTEIN_GENES_AND_PLASMIDS, ALIAS_PEGFP_N1)}
					>
						pEGFP-N1
					</NextLink>
				</PageNavbarSearchMain>
				<PageNavbarSearchSidebar>
					<BlockAutocomplete />
				</PageNavbarSearchSidebar>
			</PageNavbarSearch>
			<PlasmidElementSectionBreadcrumbs>
				<Breadcrumbs>
					<BreadcrumbItem>
						<NextLink Component={BreadcrumbItemLink} href={ROUTE_HOME}>
							{t("HOME")}
						</NextLink>
					</BreadcrumbItem>
					<BreadcrumbItem>
						<BreadcrumbItem>{t("PLASMIDS")}</BreadcrumbItem>
					</BreadcrumbItem>
				</Breadcrumbs>
			</PlasmidElementSectionBreadcrumbs>
			<LandingSimpleSection spacing={{ py: 6 }} isBorderNone>
				<LandingSimpleSection.Body>
					<Row my mx>
						<Col md={6}>
							<Paragraph sx={{ fontSize: "l", fontColor: "2" }} spacing={{ mt: 0 }}>
								Gain Unparalleled Visibility of Your Sequences
							</Paragraph>
							<UList spacing={{ ml: 5 }}>
								<UList.Item spacing={{ my: 3 }}>
									Open sequence files in SnapGene to view features, annotate, clone and use as a
									reference
								</UList.Item>
								<UList.Item spacing={{ my: 3 }}>
									Display enzyme sites, features, primers, ORFs, translations and more on plasmid
									maps or in detail on the sequence view
								</UList.Item>
								<UList.Item spacing={{ my: 3 }}>
									Annotate features on your sequences using SnapGene’s curated feature database or
									your own custom features
								</UList.Item>
								<UList.Item spacing={{ my: 3 }}>
									Add your vector directly into a cloning simulation with all restriction sites and
									features displayed
								</UList.Item>
							</UList>
						</Col>
						<Col md={6}>
							<Video
								srcMP4="/images/snapgene/plasmid-main/plasmidMain.mp4"
								poster="/images/snapgene/plasmid-main/plasmidPoster.png"
							/>
						</Col>
					</Row>
				</LandingSimpleSection.Body>
			</LandingSimpleSection>
			<SectionItems
				type="set"
				title="Plasmid Sets"
				hasDescription
				colCount={2}
				items={sets}
				ItemLinkComponent={PlasmidElementSetTitleLink}
				ItemDescriptionComponent={PlasmidElementSetDescription}
			/>
			<LandingSimpleSection spacing={{ pt: 10, pb: 24 }} isBorderNone>
				<LandingSimpleSection.Body>
					<Paragraph Tag="div" sx={{ fontSize: "l", textAlign: "center" }}>
						If you have suggestions for additional plasmids or plasmid collections, please{" "}
						<NextLink Component={Link} href={ROUTE_CONTACT_US}>
							contact us
						</NextLink>
						.
					</Paragraph>
				</LandingSimpleSection.Body>
			</LandingSimpleSection>
			<SectionTryForFree />
		</SiteLayout>
	);
};

PlasmidsHomePage.getInitialProps = resolvers;

PlasmidsHomePage.services = {
	sgApiConn: ({ sgApiConfig }) => sgApiConnection(global.fetch, sgApiConfig.baseUrl),
	sgApiClient: ({ sgApiConn }) => sgApiClient(sgApiConn),
};

PlasmidsHomePage.postConditions = [
	[({ sets }) => sets.length > 0, createAndRaise(ApplicationError.NotFound)],
];

export default pipe(
	PlasmidsHomePage,
	WithLocale({ resources }),
	WithConditions,
	NextPageWithServices(serviceCore),
	withErrorHandling,
);
